import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../utils/formatDate';
import { useTheme } from '../contexts/ThemeContext';

interface CreditCardProps {
    plan: string;
    creditsAvailable: number;
    creditsQuota: number;
    creditsConsumedMonth?: number;
    rolloverAvailable?: number;
    rolloverNextMonth?: number;
    monthlyCreditsQuota?: number;
    freeCreditsRemaining?: number;
    initialFreeCredits?: number;
    purchasedCreditsRemaining?: number;
    essentialPackBalance?: number;
    carriedPurchasedCredits?: number;
    giftCreditsRemaining?: number;
    accountStatus?: string;
    isInGracePeriod?: boolean;
}

const CreditCard: React.FC<CreditCardProps> = ({
    plan,
    creditsAvailable,
    creditsQuota,
    creditsConsumedMonth = 0,
    rolloverAvailable = 0,
    rolloverNextMonth = 0,
    monthlyCreditsQuota = 0,
    freeCreditsRemaining = 0,
    initialFreeCredits = 0,
    purchasedCreditsRemaining = 0,
    essentialPackBalance = 0,
    carriedPurchasedCredits = 0,
    giftCreditsRemaining = 0,
    accountStatus = 'ACTIVE',
    isInGracePeriod = false
}) => {
    const { t } = useTranslation();
    const { theme } = useTheme();
    const isLightMode = theme === 'light';
    const [showRolloverTooltip, setShowRolloverTooltip] = useState(false);

    const getPlanColor = () => {
        switch (plan) {
            case "ENTERPRISE": return "text-danger";
            case "PLUS":       return "text-primary";
            case "ESSENTIAL":  return "text-warning";
            default:           return "text-info";
        }
    };

    /**
     * Calcul du détail des crédits par type selon le plan.
     *
     * FREE      : affiche le solde des crédits gratuits d'inscription
     * ESSENTIAL : top-up masqué si 0 — free allocation via freeCreditsRemaining
     * PLUS      : monthly + rollover (0 à l'activation) + free allocation
     * ENTERPRISE: top-up + free allocation via freeCreditsRemaining
     */
    const getCreditsBreakdown = () => {
        switch (plan) {
            case "FREE":
                return {
                    freeCredits: creditsAvailable,
                    freeCreditsQuota: creditsQuota,
                    monthlyCredits: null,
                    topUpCredits: null,
                    rolloverCredits: null,
                    effectiveFreeCredits: null,
                };

            case "ESSENTIAL": {
                const effectiveFreeCredits = freeCreditsRemaining > 0
                    ? freeCreditsRemaining
                    : (initialFreeCredits > 0 ? Math.min(initialFreeCredits, creditsAvailable) : 0);
                const packBalance = essentialPackBalance > 0
                    ? essentialPackBalance
                    : Math.max(0, purchasedCreditsRemaining - carriedPurchasedCredits);
                const carried = carriedPurchasedCredits > 0
                    ? carriedPurchasedCredits
                    : Math.max(
                        0,
                        (purchasedCreditsRemaining > 0
                            ? purchasedCreditsRemaining
                            : Math.max(0, creditsAvailable - effectiveFreeCredits - giftCreditsRemaining))
                        - packBalance
                    );
                return {
                    freeCredits: null,
                    monthlyCredits: null,
                    essentialPackCredits: packBalance > 0 ? packBalance : null,
                    topUpCredits: carried > 0 ? carried : null,
                    topUpCreditsQuota: null,
                    rolloverCredits: null,
                    effectiveFreeCredits: effectiveFreeCredits > 0 ? effectiveFreeCredits : null,
                };
            }

            case "PLUS": {
                const monthlyQuota = monthlyCreditsQuota > 0 ? monthlyCreditsQuota : 100;
                const monthlyRemaining = Math.max(0, monthlyQuota - Math.max(0, creditsConsumedMonth));
                const rollover = rolloverAvailable || 0;
                const effectiveFreeCredits = freeCreditsRemaining > 0
                    ? freeCreditsRemaining
                    : (initialFreeCredits > 0 ? Math.min(initialFreeCredits, creditsAvailable) : null);
                // Crédits reportés (Essential, top-up…) hors quota mensuel PLUS
                const carriedPurchased = purchasedCreditsRemaining > 0
                    ? purchasedCreditsRemaining
                    : Math.max(
                        0,
                        creditsAvailable - monthlyRemaining - rollover
                        - (effectiveFreeCredits || 0) - giftCreditsRemaining
                    );
                return {
                    freeCredits: null,
                    monthlyCredits: monthlyRemaining,
                    monthlyCreditsQuota: monthlyQuota,
                    topUpCredits: carriedPurchased > 0 ? carriedPurchased : null,
                    topUpCreditsQuota: null,
                    rolloverCredits: rollover,
                    rolloverNextMonth: rolloverNextMonth || 0,
                    effectiveFreeCredits: effectiveFreeCredits && effectiveFreeCredits > 0 ? effectiveFreeCredits : null,
                };
            }

            case "ENTERPRISE": {
                const effectiveFreeCredits = freeCreditsRemaining > 0
                    ? freeCreditsRemaining
                    : (initialFreeCredits > 0 ? Math.min(initialFreeCredits, creditsAvailable) : 0);
                const topUp = purchasedCreditsRemaining > 0
                    ? purchasedCreditsRemaining
                    : Math.max(0, creditsAvailable - effectiveFreeCredits - giftCreditsRemaining);
                return {
                    freeCredits: null,
                    monthlyCredits: null,
                    topUpCredits: topUp > 0 ? topUp : null,
                    topUpCreditsQuota: null,
                    rolloverCredits: null,
                    effectiveFreeCredits: effectiveFreeCredits > 0 ? effectiveFreeCredits : null,
                };
            }

            default:
                return {
                    freeCredits: creditsAvailable,
                    freeCreditsQuota: creditsQuota,
                    monthlyCredits: null,
                    topUpCredits: null,
                    rolloverCredits: null,
                };
        }
    };

    const breakdown = getCreditsBreakdown();
    const planColor = getPlanColor();
    const cardBg = 'var(--bg-secondary)';
    const borderColor = 'var(--border-subtle)';
    const textPrimary = 'var(--text-primary)';
    const textSecondary = 'var(--text-secondary)';
    const rowBorder = isLightMode ? 'rgba(17, 24, 39, 0.12)' : 'rgba(255, 255, 255, 0.14)';

    const tooltipText = t(
        'dashboard.rolloverTooltip',
        'Les Rollover Credits sont les crédits non utilisés du cycle PLUS précédent, reportés à la fin de ce cycle (10% des crédits mensuels non consommés, max. 20). Pendant le cycle en cours, cette valeur est 0 jusqu\'à la clôture du mois.'
    );

    const topUpLabel = plan === 'PLUS'
        ? t('dashboard.plusTopUpCredits', 'Top up credits')
        : plan === 'ESSENTIAL'
            ? t('dashboard.carriedCredits', 'Carried credits (PLUS / top-ups)')
            : t('dashboard.topUpCredits', 'Purchased Credits');

    const topUpIcon = plan === 'PLUS'
        ? 'fa-solid fa-circle-plus'
        : 'fa-solid fa-credit-card';

    return (
        <div className="card mb-4 border-0" style={{ background: cardBg, border: `1px solid ${borderColor}` }}>
            <div className="card-body rounded-2 p-4" style={{ background: cardBg }}>

                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="mb-0 fw-bold" style={{ color: textPrimary }}>
                        {t('dashboard.availableCredits', 'Available Credits')}
                    </h5>
                </div>

                {/* Statut du plan */}
                <div className="mb-3 pb-2 border-bottom" style={{ borderColor: rowBorder }}>
                    <div className="d-flex justify-content-between align-items-center">
                        <span className="small" style={{ color: textSecondary }}>
                            <i className="fa-solid fa-circle-check me-2 text-success"></i>
                            {t('dashboard.currentPlan', 'Plan actuel')}
                        </span>
                        <span className={`text-uppercase fw-bold ${planColor}`}>
                            {plan}
                        </span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-2">
                        <span className="small" style={{ color: textSecondary }}>
                            <i className="fa-solid fa-circle-info me-2 text-info"></i>
                            {t('dashboard.planStatus', 'Statut')}
                        </span>
                        <span className={`fw-bold ${
                            accountStatus === 'ACTIVE'      ? 'text-success' :
                            accountStatus === 'GRACE_PERIOD'? 'text-warning' :
                            accountStatus === 'EXHAUSTED' || accountStatus === 'EXPIRED' ? 'text-danger' :
                            'text-secondary'
                        }`}>
                            {accountStatus === 'ACTIVE'       ? t('dashboard.statusActive', 'Active') :
                             accountStatus === 'GRACE_PERIOD' ? t('dashboard.statusGracePeriod', 'Grace Period') :
                             accountStatus === 'EXHAUSTED'    ? t('dashboard.statusExhausted', 'Exhausted') :
                             accountStatus === 'EXPIRED'      ? t('dashboard.statusExpired', 'Expired') :
                             accountStatus}
                        </span>
                    </div>
                </div>

                {/* Grand total crédits */}
                <div className="text-center mb-4">
                    <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
                        <i className="fa-solid fa-coins fs-3" style={{ color: '#ffc107' }}></i>
                        <h2 className={`mb-0 fw-bold ${creditsAvailable > 0 ? 'text-success' : 'text-danger'}`}>
                            {formatNumber(creditsAvailable)}
                        </h2>
                    </div>
                    <p className="small mb-0" style={{ color: textSecondary }}>
                        {plan === 'ESSENTIAL'
                            ? t('dashboard.totalCreditsWallet', 'credits available in your wallet')
                            : `${t('dashboard.outOf', 'out of')} ${formatNumber(creditsQuota)} ${t('dashboard.totalCredits', 'credits')}`}
                    </p>
                </div>

                {/* Détail crédits */}
                <div className="mb-3">

                    {/* FREE Credits (plan FREE) */}
                    {breakdown.freeCredits !== null && (
                        <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom" style={{ borderColor: rowBorder }}>
                            <span className="small" style={{ color: textSecondary }}>
                                <i className="fa-solid fa-gift me-2 text-info"></i>
                                {t('dashboard.freeCredits', 'Free Credits')}
                            </span>
                            <span className={`fw-bold ${breakdown.freeCredits > 0 ? 'text-success' : 'text-danger'}`}>
                                {formatNumber(breakdown.freeCredits)} / {formatNumber(breakdown.freeCreditsQuota || 0)}
                            </span>
                        </div>
                    )}

                    {/* Monthly Credits (PLUS) */}
                    {breakdown.monthlyCredits !== null && (
                        <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom" style={{ borderColor: rowBorder }}>
                            <span className="small" style={{ color: textSecondary }}>
                                <i className="fa-solid fa-calendar-days me-2 text-primary"></i>
                                {t('dashboard.monthlyCredits', 'Monthly Credits')}
                            </span>
                            <span className={`fw-bold ${breakdown.monthlyCredits > 0 ? 'text-success' : 'text-warning'}`}>
                                {formatNumber(breakdown.monthlyCredits)} / {formatNumber((breakdown as any).monthlyCreditsQuota || 0)}
                            </span>
                        </div>
                    )}

                    {/* Rollover Credits (PLUS) — toujours affiché, même à 0 */}
                    {plan === 'PLUS' && (
                        <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom" style={{ borderColor: rowBorder }}>
                            <span className="small d-flex align-items-center gap-1" style={{ color: textSecondary }}>
                                <i className="fa-solid fa-arrows-rotate me-2 text-success"></i>
                                {t('dashboard.rolloverCredits', 'Rollover Credits')}
                                {/* Tooltip icône */}
                                <span
                                    style={{ position: 'relative', display: 'inline-block', cursor: 'pointer' }}
                                    onMouseEnter={() => setShowRolloverTooltip(true)}
                                    onMouseLeave={() => setShowRolloverTooltip(false)}
                                    aria-label={t('dashboard.rolloverTooltipAriaLabel', 'En savoir plus sur les Rollover Credits')}
                                >
                                    <i className="fa-solid fa-circle-info" style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', opacity: 0.7 }}></i>
                                    {showRolloverTooltip && (
                                        <div style={{
                                            position: 'absolute',
                                            bottom: '130%',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            background: '#1e1e2e',
                                            color: '#fff',
                                            padding: '8px 12px',
                                            borderRadius: '8px',
                                            fontSize: '0.72rem',
                                            maxWidth: '280px',
                                            width: 'max-content',
                                            lineHeight: '1.4',
                                            zIndex: 9999,
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                                            pointerEvents: 'none',
                                            whiteSpace: 'normal',
                                        }}>
                                            {tooltipText}
                                            <div style={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                borderWidth: '5px',
                                                borderStyle: 'solid',
                                                borderColor: '#1e1e2e transparent transparent transparent',
                                            }} />
                                        </div>
                                    )}
                                </span>
                            </span>
                            <span className={`fw-bold ${(breakdown.rolloverCredits || 0) > 0 ? 'text-success' : 'text-secondary'}`}>
                                {formatNumber(breakdown.rolloverCredits || 0)}
                            </span>
                        </div>
                    )}

                    {(breakdown as any).essentialPackCredits != null && (breakdown as any).essentialPackCredits > 0 && (
                        <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom" style={{ borderColor: rowBorder }}>
                            <span className="small" style={{ color: textSecondary }}>
                                <i className="fa-solid fa-box me-2 text-warning"></i>
                                {t('dashboard.essentialPackCredits', 'ESSENTIAL pack')}
                            </span>
                            <span className="fw-bold text-warning">
                                {formatNumber((breakdown as any).essentialPackCredits)}
                            </span>
                        </div>
                    )}

                    {breakdown.topUpCredits !== null && breakdown.topUpCredits !== undefined && (
                        <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom" style={{ borderColor: rowBorder }}>
                            <span className="small" style={{ color: textSecondary }}>
                                <i className={`${topUpIcon} me-2 text-warning`}></i>
                                {topUpLabel}
                            </span>
                            <span className={`fw-bold ${breakdown.topUpCredits > 0 ? 'text-success' : 'text-danger'}`}>
                                {formatNumber(breakdown.topUpCredits)}
                                {(breakdown as any).topUpCreditsQuota ? ` / ${formatNumber((breakdown as any).topUpCreditsQuota)}` : ''}
                            </span>
                        </div>
                    )}

                    {/* Free credits allocation — tous plans sauf FREE, visible si > 0 */}
                    {(breakdown as any).effectiveFreeCredits > 0 && (
                        <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom" style={{ borderColor: rowBorder }}>
                            <span className="small" style={{ color: textSecondary }}>
                                <i className="fa-solid fa-hand-holding-heart me-2 text-info"></i>
                                {t('dashboard.freeAllocationCredits', 'Free credits allocation')}
                            </span>
                            <span className="fw-bold text-info">
                                {formatNumber((breakdown as any).effectiveFreeCredits)}
                            </span>
                        </div>
                    )}

                    {/* Bonus Credits (offerts via back-office) — tous plans, visible si > 0 */}
                    {giftCreditsRemaining > 0 && (
                        <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom" style={{ borderColor: rowBorder }}>
                            <span className="small" style={{ color: textSecondary }}>
                                <i className="fa-solid fa-star me-2" style={{ color: '#a855f7' }}></i>
                                {t('dashboard.giftCredits', 'Bonus Credits')}
                            </span>
                            <span className="fw-bold" style={{ color: '#a855f7' }}>
                                {formatNumber(giftCreditsRemaining)}
                            </span>
                        </div>
                    )}
                </div>

                {/* Grace Period Warning */}
                {isInGracePeriod && (
                    <div className="mt-3 p-2 bg-warning bg-opacity-10 rounded border border-warning border-opacity-25">
                        <p className="mb-0 text-warning small text-center fw-bold">
                            <i className="fa-solid fa-clock me-2"></i>
                            {t('dashboard.gracePeriodActive', 'Grace Period Active')}
                        </p>
                    </div>
                )}

                {/* Statut si crédits épuisés */}
                {creditsAvailable === 0 && (
                    <div className="mt-3 p-2 bg-warning bg-opacity-10 rounded border border-warning border-opacity-25">
                        <p className="mb-0 text-warning small text-center fw-bold">
                            <i className="fa-solid fa-triangle-exclamation me-2"></i>
                            {t('dashboard.readOnlyMode', 'Mode Lecture Seule')}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CreditCard;
